import { useEffect, useMemo, useState } from 'react';
import { faChevronDown } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import type { MetadataDialogKind } from '@/components/page-sections/metadata-controls/types';
import { useTagCatalog } from '@/components/page-sections/metadata-controls/use-tag-catalog';
import { StaticListItem } from '@/components/shared/sortable-list-item';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/form/search-input';
import { Icon } from '@/components/ui/icon';
import { DestructiveText, MutedText } from '@/components/ui/typography';
import type {
  DataWidgetFilterSelection,
  DataWidgetFilters,
} from '@/lib/blocks/collection/data-widget/blocks';
import { cn } from '@/lib/utils';

export const METADATA_FILTER_KIND_BY_SLOT: Record<string, MetadataDialogKind> = {
  attorneys: 'attorney',
  court: 'courts',
  practiceAreas: 'practiceAreas',
  authors: 'author',
  topics: 'topics',
};

const SEARCH_NOUN_BY_KIND: Record<MetadataDialogKind, string> = {
  attorney: 'attorneys',
  courts: 'courts',
  practiceAreas: 'practice areas',
  author: 'authors',
  topics: 'topics',
};

function useKnownSelection(
  kind: MetadataDialogKind,
  selection: DataWidgetFilterSelection,
): DataWidgetFilterSelection {
  const { tags, hasLoadedTags, ensureTagsLoaded } = useTagCatalog({ kind });

  useEffect(() => {
    ensureTagsLoaded();
  }, [ensureTagsLoaded]);

  return useMemo(() => {
    if (selection === 'all' || !hasLoadedTags) return selection;

    const knownIds = new Set(tags.map((tag) => tag.id));
    return selection.filter((id) => knownIds.has(id));
  }, [selection, tags, hasLoadedTags]);
}

export function MetadataFilterTrigger({
  kind,
  selection,
  expanded,
  onToggle,
}: {
  kind: MetadataDialogKind;
  selection: DataWidgetFilterSelection;
  expanded: boolean;
  onToggle: () => void;
}) {
  const knownSelection = useKnownSelection(kind, selection);
  const count = knownSelection === 'all' ? 0 : knownSelection.length;
  const label = count === 0 ? 'All' : `${count} selected`;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex items-center gap-1 text-sm font-medium text-primary"
    >
      {label}
      <Icon
        icon={faChevronDown}
        className={cn('size-3 transition-transform', expanded && 'rotate-180')}
      />
    </button>
  );
}

export interface MetadataFilterRow {
  slot: string;
  label: string;
}

export function MetadataFilterRows({
  rows,
  filters,
  expandedSlot,
  onExpandedSlotChange,
  onChange,
}: {
  rows: MetadataFilterRow[];
  filters: DataWidgetFilters;
  expandedSlot: string | null;
  onExpandedSlotChange: (slot: string | null) => void;
  onChange: (slot: string, next: DataWidgetFilterSelection) => void;
}) {
  return (
    <div className="space-y-1">
      {rows.map((row) => {
        const selection = filters[row.slot] ?? 'all';
        const expanded = expandedSlot === row.slot;

        return (
          <StaticListItem
            key={row.slot}
            hideDragHandle
            className="rounded-lg px-3 py-1"
            label={row.label}
            action={
              <MetadataFilterTrigger
                kind={METADATA_FILTER_KIND_BY_SLOT[row.slot]}
                selection={selection}
                expanded={expanded}
                onToggle={() => onExpandedSlotChange(expanded ? null : row.slot)}
              />
            }
            body={
              expanded ? (
                <MetadataFilterPanel
                  kind={METADATA_FILTER_KIND_BY_SLOT[row.slot]}
                  selection={selection}
                  onChange={(next) => onChange(row.slot, next)}
                />
              ) : undefined
            }
          />
        );
      })}
    </div>
  );
}

export function MetadataFilterPanel({
  kind,
  selection,
  onChange,
}: {
  kind: MetadataDialogKind;
  selection: DataWidgetFilterSelection;
  onChange: (next: DataWidgetFilterSelection) => void;
}) {
  const { tags, hasLoadedTags, isLoadingTags, loadErrorMessage, ensureTagsLoaded } = useTagCatalog({
    kind,
  });
  const [query, setQuery] = useState('');

  useEffect(() => {
    ensureTagsLoaded();
  }, [ensureTagsLoaded]);

  const visibleTags = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return tags;
    return tags.filter((tag) => tag.name.toLowerCase().includes(normalized));
  }, [tags, query]);

  const isChecked = (id: string) => selection !== 'all' && selection.includes(id);

  // Deleted and merged-away ids are left in the saved selection; a real edit is where they go.
  const toggle = (id: string) => {
    const knownIds = new Set(tags.map((tag) => tag.id));
    const current =
      selection === 'all'
        ? []
        : hasLoadedTags
          ? selection.filter((tagId) => knownIds.has(tagId))
          : selection;
    const next = current.includes(id) ? current.filter((tagId) => tagId !== id) : [...current, id];
    onChange(next);
  };

  return (
    <div className="space-y-2 pb-2">
      <SearchInput
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={`Search ${SEARCH_NOUN_BY_KIND[kind]}`}
      />
      {loadErrorMessage ? (
        <DestructiveText className="text-sm">{loadErrorMessage}</DestructiveText>
      ) : isLoadingTags && tags.length === 0 ? (
        <MutedText className="text-sm">Loading…</MutedText>
      ) : visibleTags.length === 0 ? (
        <MutedText className="text-sm">No matches found.</MutedText>
      ) : (
        <div className="space-y-1">
          {visibleTags.map((tag) => (
            <div key={tag.id} className="flex items-center justify-between gap-2 py-1 text-sm">
              <span>{tag.name}</span>
              <Checkbox
                checked={isChecked(tag.id)}
                onCheckedChange={() => toggle(tag.id)}
                aria-label={tag.name}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
